import { isExplicitForwardPeRequest } from "./chartCapabilityRegistry";

const DAY_MS = 24 * 60 * 60 * 1000;
const WINDOW_TOLERANCE_MS = 45 * DAY_MS;
const FDS_FORMULA_PATTERN = /^\s*=\s*@?FDS[A-Z]*\s*\(/i;
const PE_TEXT_PATTERN =
  /\b(?:p\s*(?:\/|-)?\s*e|pe\s+ratio|price\s*(?:\/|[-–—]?\s*to\s*[-–—]?)\s*earnings)\b/i;
const FORWARD_TEXT_PATTERN =
  /\b(?:forward|fwd|ntm|next[\s-]+(?:twelve|12)[\s-]+months?|fy\s*[-+]?\s*1)\b/i;
const FORWARD_PE_FORMULA_WRITE_TOOLS: ReadonlySet<string> = new Set([
  "modify_workbook",
  "set_range_formulas",
]);
const GENERIC_NATIVE_CHART_STEP_TERMS: ReadonlySet<string> = new Set([
  "a",
  "add",
  "an",
  "and",
  "build",
  "chart",
  "complete",
  "create",
  "current",
  "data",
  "dependent",
  "display",
  "excel",
  "final",
  "finish",
  "for",
  "from",
  "graph",
  "in",
  "insert",
  "it",
  "make",
  "metric",
  "multiple",
  "native",
  "of",
  "on",
  "plot",
  "prepare",
  "range",
  "ratio",
  "request",
  "requested",
  "selected",
  "series",
  "sheet",
  "show",
  "source",
  "step",
  "that",
  "the",
  "then",
  "this",
  "to",
  "update",
  "use",
  "using",
  "valuation",
  "verified",
  "visualisation",
  "visualise",
  "visualization",
  "visualize",
  "with",
  "workbook",
  "worksheet",
]);

export const FORWARD_PE_NO_DATA_MESSAGE =
  "FactSet returned no verified 1-year forward P/E observations for the requested period. " +
  "I did not substitute trailing/LTM P/E, and no chart was inserted.";

export interface ForwardPeRequestedWindow {
  startMs: number;
  endMs: number;
  label: string;
}

export interface ForwardPeVerifiedSourceRange {
  sheetName: string;
  address: string;
  observationCount: number;
  earliestDateMs: number;
  latestDateMs: number;
}

interface ForwardPeFormulaWriteTarget {
  sheetName: string;
  address: string;
}

export interface ForwardPeChartVerificationState {
  required: boolean;
  requestedWindow?: ForwardPeRequestedWindow;
  formulaStatus: "not_requested" | "resolved" | "unavailable";
  formulaQuery?: string;
  formulaWritten: boolean;
  formulaWriteSheetNames: string[];
  formulaWriteTargets: ForwardPeFormulaWriteTarget[];
  refreshObserved: boolean;
  refreshedSheetNames: string[];
  recalcObserved: boolean;
  verifiedSourceRanges: ForwardPeVerifiedSourceRange[];
  lastReadIssue?: string;
  chartCreated: boolean;
}

export interface ForwardPeRangeChartIntentContext {
  planStepResolved: boolean;
  planStepSignals: readonly string[];
}

export interface ForwardPeRangeChartBlock {
  ok: false;
  applied: false;
  code:
    | "forward_pe_chart_verification_required"
    | "forward_pe_data_unavailable";
  stage:
    | "formula_generation"
    | "formula_write"
    | "refresh"
    | "recalculation"
    | "readback"
    | "chart_label"
    | "data_unavailable";
  error: string;
  substitutionPerformed: false;
  chartInserted: false;
  instruction: string;
}

export function createForwardPeChartVerificationState(
  intent: string | readonly string[],
  now: Date = new Date(),
): ForwardPeChartVerificationState {
  const signals = typeof intent === "string" ? [intent] : intent;
  return {
    required: signals.some(isExplicitForwardPeRequest),
    requestedWindow: signals
      .map((signal) => parseRequestedHistoricalWindow(signal, now))
      .find(
        (window): window is ForwardPeRequestedWindow =>
          window !== undefined,
      ),
    formulaStatus: "not_requested",
    formulaWritten: false,
    formulaWriteSheetNames: [],
    formulaWriteTargets: [],
    refreshObserved: false,
    refreshedSheetNames: [],
    recalcObserved: false,
    verifiedSourceRanges: [],
    chartCreated: false,
  };
}

/**
 * Adds newly discovered forward-P/E intent while preserving upstream formula,
 * write, refresh, and recalculation evidence. A newly added historical window
 * invalidates prior read-back because that range was verified without the new
 * coverage requirement.
 */
export function updateForwardPeChartVerificationIntent(
  state: ForwardPeChartVerificationState,
  intent: string | readonly string[],
  now: Date = new Date(),
): void {
  const discovered = createForwardPeChartVerificationState(intent, now);
  if (!discovered.required) return;
  if (!state.required) {
    Object.assign(state, discovered);
    return;
  }
  if (!state.requestedWindow && discovered.requestedWindow) {
    state.requestedWindow = { ...discovered.requestedWindow };
    state.verifiedSourceRanges = [];
    state.lastReadIssue = undefined;
    state.chartCreated = false;
  }
}

/** Returns a detached, serializable copy suitable for checkpoint persistence. */
export function cloneForwardPeChartVerificationState(
  state: ForwardPeChartVerificationState,
): ForwardPeChartVerificationState {
  return {
    ...state,
    ...(state.requestedWindow
      ? { requestedWindow: { ...state.requestedWindow } }
      : {}),
    formulaWriteSheetNames: state.formulaWriteSheetNames.slice(),
    formulaWriteTargets: state.formulaWriteTargets.map((target) => ({
      ...target,
    })),
    refreshedSheetNames: state.refreshedSheetNames.slice(),
    verifiedSourceRanges: state.verifiedSourceRanges.map((range) => ({
      ...range,
    })),
  };
}

export function updateForwardPeChartVerificationState(
  state: ForwardPeChartVerificationState,
  toolName: string,
  parsedArgs: unknown,
  parsedOutput: unknown,
  rangeChartIntent?: ForwardPeRangeChartIntentContext,
): void {
  if (!state.required) return;

  if (toolName === "generate_formula") {
    updateFromGenerateFormula(state, parsedArgs, parsedOutput);
    return;
  }

  if (
    state.formulaStatus === "resolved" &&
    FORWARD_PE_FORMULA_WRITE_TOOLS.has(toolName) &&
    isSuccessfulWrite(parsedOutput)
  ) {
    const writeTargets = fdsFormulaWriteTargets(toolName, parsedArgs);
    if (writeTargets.length === 0) return;
    const writtenSheets = writeTargets.map((target) => target.sheetName);
    state.formulaWritten = true;
    state.formulaWriteSheetNames = Array.from(
      new Set([...state.formulaWriteSheetNames, ...writtenSheets]),
    );
    const targetsByKey = new Map(
      [...state.formulaWriteTargets, ...writeTargets].map((target) => [
        sourceRangeKey(target.sheetName, target.address),
        target,
      ]),
    );
    state.formulaWriteTargets = Array.from(targetsByKey.values());
    resetPostWriteEvidence(state);
    return;
  }

  if (toolName === "refresh_factset_data" && state.formulaWritten) {
    const result = recordOf(parsedOutput);
    if (
      result.ok === true &&
      result.refreshed === true &&
      result.scopeConfirmed === true
    ) {
      state.refreshObserved = true;
      state.refreshedSheetNames = Array.isArray(result.refreshedSheetNames)
        ? result.refreshedSheetNames.map(stringValue).filter(Boolean)
        : [];
      state.recalcObserved = false;
      state.verifiedSourceRanges = [];
      state.lastReadIssue = undefined;
    }
    return;
  }

  if (
    toolName === "recalculate_workbook" &&
    state.formulaWritten &&
    state.refreshObserved
  ) {
    const result = recordOf(parsedOutput);
    if (result.ok === true && result.recalculated === true) {
      state.recalcObserved = true;
      state.verifiedSourceRanges = [];
      state.lastReadIssue = undefined;
    }
    return;
  }

  if (toolName === "read_range" && state.recalcObserved) {
    const evaluation = evaluateForwardPeReadback(
      parsedArgs,
      parsedOutput,
      state.requestedWindow,
    );
    if (evaluation.evidence) {
      const key = sourceRangeKey(
        evaluation.evidence.sheetName,
        evaluation.evidence.address,
      );
      state.verifiedSourceRanges = [
        ...state.verifiedSourceRanges.filter(
          (entry) => sourceRangeKey(entry.sheetName, entry.address) !== key,
        ),
        evaluation.evidence,
      ];
      state.lastReadIssue = undefined;
    } else if (evaluation.relevant) {
      state.lastReadIssue = evaluation.issue;
    }
    return;
  }

  if (
    (toolName === "range_chart" || toolName === "native_graph") &&
    classifyRangeChartForwardPeTarget(
      state,
      parsedArgs,
      rangeChartIntent,
    ) === "forward_pe" &&
    isSuccessfulWrite(parsedOutput)
  ) {
    state.chartCreated = true;
  }
}

export function getForwardPeRangeChartBlock(
  state: ForwardPeChartVerificationState,
  rawInput: unknown,
  rangeChartIntent?: ForwardPeRangeChartIntentContext,
): ForwardPeRangeChartBlock | null {
  if (
    !state.required ||
    classifyRangeChartForwardPeTarget(
      state,
      rawInput,
      rangeChartIntent,
    ) === "unrelated"
  ) {
    return null;
  }

  if (state.formulaStatus === "unavailable") {
    return block(
      "forward_pe_data_unavailable",
      "data_unavailable",
      FORWARD_PE_NO_DATA_MESSAGE,
      "Do not call range_chart, native_chart, or active_graph for this metric. Mark the dependent chart step failed " +
        "and return a partial/failed result with the no-data and no-substitution explanation.",
    );
  }
  if (state.formulaStatus !== "resolved") {
    return block(
      "forward_pe_chart_verification_required",
      "formula_generation",
      "The exact forward-P/E formula has not been resolved, so no chart was inserted.",
      "Call generate_formula with one concise query containing the company, explicit forward/NTM basis, " +
        "frequency, and full historical window. Do not use VAL_PE2.",
    );
  }
  if (!state.formulaWritten) {
    return block(
      "forward_pe_chart_verification_required",
      "formula_write",
      "The generated forward-P/E series has not been written to the workbook, so no chart was inserted.",
      "Write the exact generated Date and forward-P/E series to a blank source range before charting.",
    );
  }
  if (!state.refreshObserved) {
    return block(
      "forward_pe_chart_verification_required",
      "refresh",
      "The forward-P/E source formulas have not been refreshed, so no chart was inserted.",
      "Refresh the FactSet formulas once before recalculation and read-back.",
    );
  }
  if (!state.recalcObserved) {
    return block(
      "forward_pe_chart_verification_required",
      "recalculation",
      "The refreshed forward-P/E source formulas have not been recalculated, so no chart was inserted.",
      "Recalculate the workbook, then read back the complete Date/forward-P/E source range.",
    );
  }

  const input = recordOf(rawInput);
  const title = rangeChartTitle(input);
  if (
    !title ||
    !PE_TEXT_PATTERN.test(title) ||
    !FORWARD_TEXT_PATTERN.test(title)
  ) {
    return block(
      "forward_pe_chart_verification_required",
      "chart_label",
      "The chart is not explicitly labeled as forward/NTM P/E, so no chart was inserted.",
      "Set a chart title that identifies the exact forward basis, such as “1-Year Forward P/E”; " +
        "do not use a plain or trailing/LTM P/E label.",
    );
  }
  const sheetName = stringValue(input.sheetName);
  const sourceRange = stringValue(input.sourceRange);
  const matchingEvidence =
    sheetName && sourceRange
      ? state.verifiedSourceRanges.find((entry) =>
          verifiedRangeCoversSource(
            entry,
            sheetName,
            sourceRange,
            state.formulaWriteSheetNames,
            state.formulaWriteTargets,
            state.refreshedSheetNames,
          ),
        )
      : undefined;
  if (!matchingEvidence) {
    return block(
      "forward_pe_chart_verification_required",
      "readback",
      state.lastReadIssue ??
        "The exact native-chart source range has not passed forward-basis, observation, and full-window read-back verification.",
      "Read the exact chart source range in JSON format after refresh/recalculation. It must include Date " +
        "and explicitly forward/NTM P/E headers, aligned numeric observations, no truncation, and the full requested lookback.",
    );
  }

  return null;
}

function updateFromGenerateFormula(
  state: ForwardPeChartVerificationState,
  parsedArgs: unknown,
  parsedOutput: unknown,
): void {
  const args = recordOf(parsedArgs);
  const argQueries = Array.isArray(args.queries)
    ? args.queries.map(stringValue)
    : [stringValue(args.query)];
  const output = recordOf(parsedOutput);
  const rawResults = Array.isArray(output.results)
    ? output.results
    : [output];
  const results = rawResults
    .map((entry, index) => {
      const result = recordOf(entry);
      return {
        result,
        query: stringValue(result.query) || argQueries[index] || "",
      };
    })
    .filter(({ query }) => isExplicitForwardPeRequest(query));
  if (results.length === 0) return;

  const resolved = results.find(({ result }) =>
    Boolean(stringValue(result.formula) || stringValue(result.formula_template)),
  );
  if (resolved) {
    state.formulaStatus = "resolved";
    state.formulaQuery = resolved.query;
    state.formulaWritten = false;
    state.formulaWriteSheetNames = [];
    state.formulaWriteTargets = [];
    resetPostWriteEvidence(state);
    return;
  }
  if (results.every(({ result }) => result.dataUnavailable === true)) {
    state.formulaStatus = "unavailable";
    state.formulaQuery = results[0]?.query;
    state.formulaWritten = false;
    state.formulaWriteSheetNames = [];
    state.formulaWriteTargets = [];
    resetPostWriteEvidence(state);
  }
}

function resetPostWriteEvidence(state: ForwardPeChartVerificationState): void {
  state.refreshObserved = false;
  state.refreshedSheetNames = [];
  state.recalcObserved = false;
  state.verifiedSourceRanges = [];
  state.lastReadIssue = undefined;
  state.chartCreated = false;
}

function evaluateForwardPeReadback(
  parsedArgs: unknown,
  parsedOutput: unknown,
  requestedWindow: ForwardPeRequestedWindow | undefined,
): {
  relevant: boolean;
  evidence?: ForwardPeVerifiedSourceRange;
  issue?: string;
} {
  const args = recordOf(parsedArgs);
  const output = recordOf(parsedOutput);
  const sheetName =
    stringValue(output.sheetName) || stringValue(args.sheetName);
  const address = stringValue(output.address) || stringValue(args.address);
  const values = output.values;
  if (!sheetName || !address || !Array.isArray(values)) {
    return { relevant: false };
  }
  if (output.truncated === true) {
    return {
      relevant: true,
      issue:
        "The forward-P/E source read was truncated, so full-window coverage is not verified and no chart was inserted.",
    };
  }

  const rows = values.filter(Array.isArray) as unknown[][];
  const header = findForwardPeHeaders(rows);
  if (!header) {
    return {
      relevant: true,
      issue:
        "The read-back did not include both a Date header and an explicitly forward/NTM P/E header, so the metric basis is not verified.",
    };
  }

  const observations = rows
    .slice(header.rowIndex + 1)
    .map((row) => ({
      dateMs: parseDateCell(row[header.dateColumn]),
      value: parseNumericCell(row[header.metricColumn]),
    }))
    .filter(
      (row): row is { dateMs: number; value: number } =>
        row.dateMs !== undefined && row.value !== undefined,
    );
  if (observations.length < 2) {
    return {
      relevant: true,
      issue:
        "The forward-P/E source read did not contain at least two aligned usable Date/value observations, so no chart was inserted.",
    };
  }

  const dates = observations.map((row) => row.dateMs);
  const earliestDateMs = Math.min(...dates);
  const latestDateMs = Math.max(...dates);
  if (
    requestedWindow &&
    earliestDateMs > requestedWindow.startMs + WINDOW_TOLERANCE_MS
  ) {
    return {
      relevant: true,
      issue:
        `The verified forward-P/E series starts after the requested ${requestedWindow.label} window. ` +
        "The lookback was not shortened and no chart was inserted.",
    };
  }
  if (
    requestedWindow &&
    latestDateMs < requestedWindow.endMs - WINDOW_TOLERANCE_MS
  ) {
    return {
      relevant: true,
      issue:
        `The verified forward-P/E series ends before the requested ${requestedWindow.label} window. ` +
        "The lookback was not shortened and no chart was inserted.",
    };
  }

  return {
    relevant: true,
    evidence: {
      sheetName,
      address,
      observationCount: observations.length,
      earliestDateMs,
      latestDateMs,
    },
  };
}

function findForwardPeHeaders(
  rows: readonly unknown[][],
):
  | { rowIndex: number; dateColumn: number; metricColumn: number }
  | undefined {
  for (let rowIndex = 0; rowIndex < Math.min(rows.length, 6); rowIndex += 1) {
    const row = rows[rowIndex];
    const dateColumn = row.findIndex(
      (cell) =>
        typeof cell === "string" &&
        /\b(?:date|period|as[\s-]*of)\b/i.test(cell),
    );
    const metricColumn = row.findIndex(
      (cell) =>
        typeof cell === "string" &&
        PE_TEXT_PATTERN.test(cell) &&
        FORWARD_TEXT_PATTERN.test(cell),
    );
    if (dateColumn >= 0 && metricColumn >= 0 && dateColumn !== metricColumn) {
      return { rowIndex, dateColumn, metricColumn };
    }
  }
  return undefined;
}

function parseRequestedHistoricalWindow(
  request: string,
  now: Date,
): ForwardPeRequestedWindow | undefined {
  const relative = /\b(?:last|past|previous|prior)\s+([a-z]+|\d+)\s*(years?|yrs?|months?|mos?)\b/i.exec(
    request,
  );
  if (relative) {
    const count = parseCount(relative[1]);
    if (count && count > 0) {
      const end = new Date(now.getTime());
      const start = new Date(now.getTime());
      if (/^(?:years?|yrs?)$/i.test(relative[2])) {
        start.setUTCFullYear(start.getUTCFullYear() - count);
      } else {
        start.setUTCMonth(start.getUTCMonth() - count);
      }
      return {
        startMs: start.getTime(),
        endMs: end.getTime(),
        label: `${count}-${/^(?:years?|yrs?)$/i.test(relative[2]) ? "year" : "month"}`,
      };
    }
  }

  const sinceYear = /\bsince\s+((?:19|20)\d{2})\b/i.exec(request);
  if (sinceYear) {
    const year = Number(sinceYear[1]);
    return {
      startMs: Date.UTC(year, 0, 1),
      endMs: now.getTime(),
      label: `since-${year}`,
    };
  }
  return undefined;
}

function parseCount(value: string): number | undefined {
  if (/^\d+$/.test(value)) return Number(value);
  const words: Record<string, number> = {
    one: 1,
    two: 2,
    three: 3,
    four: 4,
    five: 5,
    six: 6,
    seven: 7,
    eight: 8,
    nine: 9,
    ten: 10,
  };
  return words[value.toLowerCase()];
}

function parseDateCell(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    if (Number.isInteger(value) && value >= 19_000_101 && value <= 29_991_231) {
      return parseYyyyMmDd(String(value));
    }
    if (value > 1 && value < 2_958_466) {
      return Math.round((value - 25_569) * DAY_MS);
    }
    return undefined;
  }
  if (typeof value !== "string" || !value.trim()) return undefined;
  const trimmed = value.trim();
  if (/^\d{8}$/.test(trimmed)) return parseYyyyMmDd(trimmed);
  if (/^\d+(?:\.\d+)?$/.test(trimmed)) {
    return parseDateCell(Number(trimmed));
  }
  const parsed = Date.parse(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function parseYyyyMmDd(value: string): number | undefined {
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(4, 6));
  const day = Number(value.slice(6, 8));
  const parsed = Date.UTC(year, month - 1, day);
  const date = new Date(parsed);
  return date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
    ? parsed
    : undefined;
}

function parseNumericCell(value: unknown): number | undefined {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value !== "string") return undefined;
  const normalized = value.trim().replace(/,/g, "").replace(/x$/i, "");
  if (!/^[-+]?(?:\d+\.?\d*|\.\d+)$/.test(normalized)) return undefined;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : undefined;
}

type RangeChartForwardPeTarget =
  | "forward_pe"
  | "unrelated"
  | "ambiguous";

function classifyRangeChartForwardPeTarget(
  state: ForwardPeChartVerificationState,
  rawInput: unknown,
  intent?: ForwardPeRangeChartIntentContext,
): RangeChartForwardPeTarget {
  const input = recordOf(rawInput);
  if (
    input.operation !== "create_range_chart" &&
    input.operation !== "create_native_graph"
  ) {
    return "unrelated";
  }
  const planStepSignals = (intent?.planStepSignals ?? [])
    .map((signal) => signal.trim())
    .filter(Boolean);
  if (planStepSignals.some(isExplicitForwardPeRequest)) {
    return "forward_pe";
  }
  if (rangeChartSourceMatchesForwardPeEvidence(state, input)) {
    return "forward_pe";
  }
  const title = rangeChartTitle(input);
  if (title) {
    return PE_TEXT_PATTERN.test(title) ? "forward_pe" : "unrelated";
  }
  if (
    intent?.planStepResolved &&
    hasSpecificNonForwardRangeChartIntent(planStepSignals)
  ) {
    return "unrelated";
  }
  return "ambiguous";
}

function rangeChartSourceMatchesForwardPeEvidence(
  state: ForwardPeChartVerificationState,
  input: Record<string, unknown>,
): boolean {
  const sheetName = stringValue(input.sheetName);
  const sourceRange = stringValue(input.sourceRange);
  if (!sheetName || !sourceRange) return false;
  const sameSheet = (candidate: string): boolean =>
    normalizeSheetName(candidate) === normalizeSheetName(sheetName);
  return (
    state.formulaWriteTargets.some(
      (target) =>
        sameSheet(target.sheetName) &&
        a1RangeContains(sourceRange, target.address),
    ) ||
    state.verifiedSourceRanges.some(
      (evidence) =>
        sameSheet(evidence.sheetName) &&
        a1RangeContains(evidence.address, sourceRange),
    )
  );
}

function hasSpecificNonForwardRangeChartIntent(
  signals: readonly string[],
): boolean {
  const combined = signals.join(" ");
  if (
    PE_TEXT_PATTERN.test(combined) ||
    FORWARD_TEXT_PATTERN.test(combined)
  ) {
    return false;
  }
  const withoutAddresses = combined.replace(
    /\$?[A-Z]{1,3}\$?\d+(?::\$?[A-Z]{1,3}\$?\d+)?/gi,
    " ",
  );
  const terms = withoutAddresses.toLowerCase().match(/[a-z0-9]+/g) ?? [];
  return terms.some(
    (term) =>
      !/^\d+$/.test(term) &&
      !GENERIC_NATIVE_CHART_STEP_TERMS.has(term),
  );
}

function rangeChartTitle(
  input: Record<string, unknown>,
): string {
  return [
    stringValue(input.title),
    stringValue(recordOf(recordOf(input.chartInfo).title).text),
  ]
    .filter(Boolean)
    .join(" ");
}

function verifiedRangeCoversSource(
  evidence: ForwardPeVerifiedSourceRange,
  sheetName: string,
  sourceRange: string,
  formulaWriteSheetNames: readonly string[],
  formulaWriteTargets: readonly ForwardPeFormulaWriteTarget[],
  refreshedSheetNames: readonly string[],
): boolean {
  if (normalizeSheetName(evidence.sheetName) !== normalizeSheetName(sheetName)) {
    return false;
  }
  if (
    !formulaWriteSheetNames.some(
      (name) => normalizeSheetName(name) === normalizeSheetName(sheetName),
    ) ||
    !refreshedSheetNames.some(
      (name) => normalizeSheetName(name) === normalizeSheetName(sheetName),
    )
  ) {
    return false;
  }
  const readRange = parseA1Range(evidence.address);
  const chartRange = parseA1Range(sourceRange);
  const chartContainsFormulaAnchor = formulaWriteTargets.some((target) => {
    if (normalizeSheetName(target.sheetName) !== normalizeSheetName(sheetName)) {
      return false;
    }
    return a1RangeContains(sourceRange, target.address);
  });
  return Boolean(
    readRange &&
      chartRange &&
      chartContainsFormulaAnchor &&
      a1RangeContains(evidence.address, sourceRange),
  );
}

function a1RangeContains(
  outerAddress: string,
  innerAddress: string,
): boolean {
  const outer = parseA1Range(outerAddress);
  const inner = parseA1Range(innerAddress);
  return Boolean(
    outer &&
      inner &&
      outer.startRow <= inner.startRow &&
      outer.endRow >= inner.endRow &&
      outer.startColumn <= inner.startColumn &&
      outer.endColumn >= inner.endColumn,
  );
}

function parseA1Range(
  address: string,
):
  | {
      startRow: number;
      endRow: number;
      startColumn: number;
      endColumn: number;
    }
  | undefined {
  const local = address.slice(address.lastIndexOf("!") + 1);
  const [startRaw, endRaw = startRaw] = local.split(":");
  const start = parseA1Cell(startRaw);
  const end = parseA1Cell(endRaw);
  if (!start || !end) return undefined;
  return {
    startRow: Math.min(start.row, end.row),
    endRow: Math.max(start.row, end.row),
    startColumn: Math.min(start.column, end.column),
    endColumn: Math.max(start.column, end.column),
  };
}

function parseA1Cell(
  address: string | undefined,
): { row: number; column: number } | undefined {
  const match = /^([A-Z]+)(\d+)$/i.exec(
    address?.replace(/\$/g, "").trim() ?? "",
  );
  if (!match) return undefined;
  let column = 0;
  for (const char of match[1].toUpperCase()) {
    column = column * 26 + char.charCodeAt(0) - 64;
  }
  return { row: Number(match[2]) - 1, column: column - 1 };
}

function containsFdsFormula(value: unknown): boolean {
  if (typeof value === "string") return FDS_FORMULA_PATTERN.test(value);
  if (Array.isArray(value)) return value.some(containsFdsFormula);
  if (!value || typeof value !== "object") return false;
  return Object.values(value as Record<string, unknown>).some(
    containsFdsFormula,
  );
}

function fdsFormulaWriteTargets(
  toolName: string,
  value: unknown,
): ForwardPeFormulaWriteTarget[] {
  if (toolName === "set_range_formulas") {
    const input = recordOf(value);
    const sheetName = stringValue(input.sheetName);
    const address = stringValue(input.address);
    return sheetName &&
      address &&
      containsFdsFormula(input.formulas)
      ? [{ sheetName, address }]
      : [];
  }
  if (toolName !== "modify_workbook") return [];

  const targets = new Map<string, ForwardPeFormulaWriteTarget>();
  const input = recordOf(value);
  const fallbackSheetName = stringValue(input.targetSheet);
  const modifications = recordOf(input.modifications);
  const worksheets = Array.isArray(modifications.worksheets)
    ? modifications.worksheets
    : [];
  const addTarget = (sheetName: string, address: string): void => {
    if (!sheetName || !address) return;
    const target = { sheetName, address };
    targets.set(sourceRangeKey(sheetName, address), target);
  };
  for (const worksheetValue of worksheets) {
    const worksheet = recordOf(worksheetValue);
    const sheetName =
      stringValue(worksheet.sheetName) || fallbackSheetName;
    if (!sheetName) continue;
    const cells = Array.isArray(worksheet.cells) ? worksheet.cells : [];
    for (const cellValue of cells) {
      const cell = recordOf(cellValue);
      if (
        cell.action === "set" &&
        FDS_FORMULA_PATTERN.test(stringValue(cell.formula))
      ) {
        addTarget(sheetName, stringValue(cell.address));
      }
    }
    const ranges = Array.isArray(worksheet.ranges) ? worksheet.ranges : [];
    for (const rangeValue of ranges) {
      const range = recordOf(rangeValue);
      if (
        range.action === "fill" &&
        containsFdsFormula(range.formulas)
      ) {
        addTarget(sheetName, stringValue(range.address));
      }
    }
  }
  return Array.from(targets.values());
}

function isSuccessfulWrite(output: unknown): boolean {
  const result = recordOf(output);
  return result.ok === true && result.applied === true && !result.error;
}

function sourceRangeKey(sheetName: string, address: string): string {
  return `${normalizeSheetName(sheetName)}!${address.trim().toUpperCase()}`;
}

function normalizeSheetName(value: string): string {
  return value.trim().toLowerCase();
}

function recordOf(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function stringValue(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function block(
  code: ForwardPeRangeChartBlock["code"],
  stage: ForwardPeRangeChartBlock["stage"],
  error: string,
  instruction: string,
): ForwardPeRangeChartBlock {
  return {
    ok: false,
    applied: false,
    code,
    stage,
    error,
    substitutionPerformed: false,
    chartInserted: false,
    instruction,
  };
}
